/* global axios */
// DahReply — all external knowledge API calls are proxied through the Rails backend.
// The proxy controller at Api::V1::Accounts::ProxyController#knowledge injects
// the real KNOWLEDGE_API_KEY server-side, so no secret is exposed to the browser.
// Firecrawl crawl requests are also proxied (see api/integrations/firecrawl.js),
// so the Firecrawl API key is never exposed client-side either.

import { useAlert } from 'dashboard/composables';
import firecrawlApi from '../../api/integrations/firecrawl';

// ---------------------------------------------------------------------------
// Proxy URL helper
// All knowledgeApi paths are forwarded through /api/v1/accounts/:id/proxy/knowledge
// The global `axios` instance (configured by APIHelper) already carries Chatwoot
// session auth headers (access-token / client / uid), so no extra header setup needed.
// ---------------------------------------------------------------------------
const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

// Lightrag-only, ignored for dify. Omitting a field keeps the KB's existing
// default for it — only append what the user actually set.
const appendAdvancedUploadOptions = (formData, options) => {
  if (!options) return;
  const { chunkWordSize, chunkOverlapWordSize, delimiter } = options;
  if (chunkWordSize !== null && chunkWordSize !== undefined && chunkWordSize !== '') {
    formData.append('chunk_word_size', chunkWordSize);
  }
  if (chunkOverlapWordSize !== null && chunkOverlapWordSize !== undefined && chunkOverlapWordSize !== '') {
    formData.append('chunk_overlap_word_size', chunkOverlapWordSize);
  }
  if (delimiter) {
    // The UI shows the newline default as the literal, visible two-character
    // escape sequence "\n" (a raw newline is invisible in a text input) —
    // resolve it back to an actual newline byte before sending.
    formData.append('delimiter', delimiter === '\\n' ? '\n' : delimiter);
  }
};

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------
export const state = {
  uploadedFile: null, // Kept for backward compatibility
  uploadedFiles: [], // New array to hold multiple files
  knowledgeData: null,
  indexing_technique: 'high_quality',
  process_rule: {},
  isUploaded: false,
  datasetId: null,
  crawlResult: {},
  knowledgeList: [],
  tableBody: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 10,
  },
  activeKnowledgeList: [],
  restrictionStatus: null,
  knowledgeBaseName: null,
  knowledgeEngine: 'lightrag',
  // graph_enabled/images_enabled are lightrag-only and creation-time-only
  // (no edit path afterward) — default to true to match the server's own
  // default.
  knowledgeGraphEnabled: true,
  knowledgeImagesEnabled: true,
  // Per-upload overrides, lightrag-only, ignored for dify. null/'' means
  // "omit — keep the KB's existing defaults", matching the API's own
  // omit-to-default semantics.
  advancedUploadOptions: {
    chunkWordSize: null,
    chunkOverlapWordSize: null,
    delimiter: null,
  },
  uploadErrors: [], // To store upload errors
  currentDocument: null,
  knowledgeDetails: {},
};

// ---------------------------------------------------------------------------
// Getters
// ---------------------------------------------------------------------------
export const getters = {
  getKnowledgeList(state) {
    return state.knowledgeList;
  },
  getPagination(state) {
    return state.pagination;
  },
  getActiveKnowledgeList(state) {
    return state.activeKnowledgeList;
  },
  getRestrictionStatus(state) {
    return state.restrictionStatus;
  },
};

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------
export const mutations = {
  setIsUploaded(state, status) {
    state.isUploaded = status;
  },
  setKnowledgeDetails(state, details) {
    state.knowledgeDetails = details;
  },
  setCurrentDocument(state, document) {
    state.currentDocument = document;
  },
  setUploadedFile(state, file) {
    state.uploadedFile = file;
    state.uploadedFiles = [file];
    state.isUploaded = true;
  },
  setUploadedFiles(state, files) {
    state.uploadedFiles = files;
    state.uploadedFile = files.length > 0 ? files[0] : null;
    state.isUploaded = files.length > 0;
  },
  appendUploadedFiles(state, files) {
    state.uploadedFiles = [...state.uploadedFiles, ...files];
    state.uploadedFile =
      state.uploadedFiles.length > 0 ? state.uploadedFiles[0] : null;
    state.isUploaded = state.uploadedFiles.length > 0;
  },
  removeUploadedFileAt(state, index) {
    state.uploadedFiles = state.uploadedFiles.filter((_, i) => i !== index);
    state.uploadedFile =
      state.uploadedFiles.length > 0 ? state.uploadedFiles[0] : null;
    state.isUploaded = state.uploadedFiles.length > 0;
  },
  removeUploadedFile(state) {
    state.uploadedFile = null;
    state.uploadedFiles = [];
    state.isUploaded = false;
    state.uploadErrors = [];
  },
  removeCrawlResult(state) {
    state.crawlResult = {};
    state.isUploaded = false;
  },
  setDatasetId(state, id) {
    state.datasetId = id;
  },
  addUploadError(state, error) {
    state.uploadErrors.push(error);
  },
  clearUploadErrors(state) {
    state.uploadErrors = [];
  },
  setCrawlResult(state, result) {
    state.crawlResult = result;
  },
  updateCrawlResult(state, result) {
    state.crawlResult.data = result;
  },
  setKnowledgeList(state, knowledgeList) {
    state.knowledgeList = knowledgeList;
  },
  setTableBody(state, data) {
    state.tableBody = [...data];
  },
  setPagination(state, { currentPage, totalPages, totalItems, pageSize }) {
    state.pagination = { currentPage, totalPages, totalItems, pageSize };
  },
  setActiveKnowledgeList(state, activeKnowledgeList) {
    state.activeKnowledgeList = activeKnowledgeList;
  },
  setRestrictionStatus(state, status) {
    state.restrictionStatus = status;
  },
  setKnowledgeBaseName(state, name) {
    state.knowledgeBaseName = name;
  },
  setKnowledgeEngine(state, engine) {
    state.knowledgeEngine = engine;
  },
  setKnowledgeGraphEnabled(state, enabled) {
    state.knowledgeGraphEnabled = enabled;
  },
  setKnowledgeImagesEnabled(state, enabled) {
    state.knowledgeImagesEnabled = enabled;
  },
  setAdvancedUploadOptions(state, options) {
    state.advancedUploadOptions = { ...state.advancedUploadOptions, ...options };
  },
  resetAdvancedUploadOptions(state) {
    state.advancedUploadOptions = {
      chunkWordSize: null,
      chunkOverlapWordSize: null,
      delimiter: null,
    };
  },
  resetPagination(state) {
    state.pagination = { currentPage: 1, totalPages: 1, totalItems: 0, pageSize: 10 };
  },
};

export const actions = {
  updateKnowledgeBaseName({ commit }, name) {
    commit('setKnowledgeBaseName', name);
  },

  updateKnowledgeEngine({ commit }, engine) {
    commit('setKnowledgeEngine', engine);
  },

  updateKnowledgeGraphEnabled({ commit }, enabled) {
    commit('setKnowledgeGraphEnabled', enabled);
  },

  updateKnowledgeImagesEnabled({ commit }, enabled) {
    commit('setKnowledgeImagesEnabled', enabled);
  },

  updateAdvancedUploadOptions({ commit }, options) {
    commit('setAdvancedUploadOptions', options);
  },

  resetAdvancedUploadOptions({ commit }) {
    commit('resetAdvancedUploadOptions');
  },

  async fetchKnowledgeList({ rootGetters, commit }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const { data } = await axios.get(
        knowledgeProxy(accountId, `/chatbot/accounts/${accountId}/datasets`)
      );
      commit('setKnowledgeList', data.data);
    } catch (error) {
      console.error('Error fetching knowledge datasets:', error);
      throw error;
    }
  },

  async deleteKnowledge({ rootGetters }, { dataset_id }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      await axios.delete(
        knowledgeProxy(accountId, `/chatbot/accounts/${accountId}/datasets/${dataset_id}`)
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  async createKnowledgeBase({ state, rootGetters, commit }) {
    const accountId = rootGetters.getCurrentAccountId;
    const title = state.knowledgeBaseName;
    try {
      const { data } = await axios.post(
        knowledgeProxy(accountId, `/chatbot/accounts/${accountId}/datasets`),
        {
          name: title,
          engine: state.knowledgeEngine || 'lightrag',
          // Ignored server-side for dify KBs — safe to always send.
          graph_enabled: state.knowledgeGraphEnabled,
          images_enabled: state.knowledgeImagesEnabled,
        }
      );
      commit('setDatasetId', data.dataset_id);
    } catch (error) {
      if (error.response?.status === 409) {
        throw new Error('A knowledge base with this name already exists.');
      }
      // The real API's validation errors (422) return `detail` as an array of
      // { loc, msg, type } objects, not a string - stringifying the array
      // directly (the old `|| fallback` here did) renders as "[object Object]".
      const { detail } = error.response?.data || {};
      let message = 'Error creating knowledge base.';
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail) && detail.length) {
        message = detail.map(item => item.msg).filter(Boolean).join(', ') || message;
      }
      throw new Error(message);
    }
  },

  async uploadDocument({ state, rootGetters, commit }) {
    if ((!state.uploadedFiles || state.uploadedFiles.length === 0) && !state.uploadedFile) {
      throw new Error('uploadDocument: No files to upload');
    }
    if (!state.datasetId) {
      throw new Error('uploadDocument: Missing datasetId');
    }

    const accountId = rootGetters.getCurrentAccountId;
    commit('clearUploadErrors');

    const filesToUpload = state.uploadedFiles.length
      ? state.uploadedFiles
      : [state.uploadedFile];

    let successCount = 0;

    for (const file of filesToUpload) {
      const formData = new FormData();
      formData.append('file', file);
      appendAdvancedUploadOptions(formData, state.advancedUploadOptions);
      try {
        await axios.post(
          knowledgeProxy(accountId, `/chatbot/account/${accountId}/datasets/${state.datasetId}/documents`),
          formData
          // No Content-Type header — axios sets multipart boundary automatically
        );
        successCount += 1;
      } catch (error) {
        let errorMsg;
        if (error.response?.status === 413) {
          errorMsg = `File "${file.name}" is too large. The server rejected it.`;
        } else if (error.response?.status === 402) {
          // Lightrag ingestion limits (word limit / credits / etc.) — no field
          // distinguishes them, just surface the server's message verbatim as a
          // blocking toast, since this action's callers don't otherwise alert.
          errorMsg = error.response?.data?.message || `File "${file.name}" was rejected.`;
        } else {
          errorMsg = `Error uploading "${file.name}": ${error.response?.statusText || error.message}`;
        }
        commit('addUploadError', errorMsg);
        useAlert(errorMsg);
      }
    }

    if (successCount === 0) {
      throw new Error('All documents failed to upload.');
    }
  },

  handleFileUpload({ commit }, file) {
    commit('removeUploadedFile');
    if (file) commit('setUploadedFile', file);
  },

  handleFilesUpload({ commit }, files) {
    if (files && files.length > 0) commit('appendUploadedFiles', files);
  },

  removeUploadedFileAt({ commit }, index) {
    commit('removeUploadedFileAt', index);
  },

  // Firecrawl — still direct (Batch 5)
  async handleCrawl({ dispatch }, payload) {
    const { url, limit, maxDepth, useSiteMap, onlyMainContent, includedPath, excludedPath } = payload;
    try {
      const body = {
        url,
        excludePaths: [excludedPath || ''],
        includePaths: [includedPath || ''],
        maxDepth: Number(maxDepth),
        ignoreSitemap: !useSiteMap,
        limit: Number(limit),
        scrapeOptions: {
          formats: ['markdown'],
          includeTags: [''],
          excludeTags: [''],
          onlyMainContent,
          removeBase64Images: true,
          waitFor: 1000,
        },
      };

      const { data } = await firecrawlApi.crawl(body);

      // Start polling the results
      await dispatch('processCrawlResults', { id: data.id, url });

      return data;
    } catch (err) {
      console.error('Error in handleCrawl:', err.message || err);
      throw err;
    }
  },

  async processCrawlResults({ commit, dispatch }, { id, url }) {
    try {
      const { data } = await firecrawlApi.status(id);

      if (!data.success) {
        throw new Error('Error fetching crawl results');
      }

      commit('setCrawlResult', data);

      if (data.status === 'completed') {
        commit('setIsUploaded', true);
        dispatch('recordCrawlUsage', { url, totalPages: data.data.length });
      } else if (data.status === 'scraping') {
        // Poll every second until scraping completes
        setTimeout(() => dispatch('processCrawlResults', { id, url }), 1000);
      } else {
        throw new Error(`Crawl did not complete (status: ${data.status}).`);
      }
    } catch (err) {
      console.error('Error in processCrawlResults:', err.message || err);
      throw err;
    }
  },

  async recordCrawlUsage({ rootGetters }, payload) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      await axios.post(
        knowledgeProxy(accountId, `/crawl/${accountId}/record`),
        { url: payload.url, pages_crawled: payload.totalPages }
      );
    } catch (err) {
      useAlert(err);
      console.error('Error updating FireCrawl usage:', err.message || err);
    }
  },

  async convertToFile({ state, commit }) {
    if (!state.crawlResult?.data?.length) {
      throw new Error('convertToFile: No crawl results to convert');
    }
    const filesToUpload = state.crawlResult.data.map((result, index) => {
      if (!result.markdown) throw new Error(`Missing markdown at index ${index}`);
      const title = result?.metadata?.title || `untitled-${index}`;
      const sanitizedTitle = title.replace(/[/\\:*?"<>|]/g, '');
      const blob = new Blob([result.markdown], { type: 'text/plain' });
      return new File([blob], `${sanitizedTitle}.txt`, { type: 'text/plain' });
    });
    commit('setUploadedFiles', filesToUpload);
  },

  handleUpdateCrawlResult({ commit }, result) {
    commit('updateCrawlResult', result);
  },

  async uploadFile({ rootGetters, state }, file) {
    const accountId = rootGetters.getCurrentAccountId;
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(
        knowledgeProxy(accountId, `/chatbot/account/${accountId}/datasets/${state.datasetId}/documents`),
        formData
      );
    } catch (err) {
      console.error('Error uploading document:', err);
      throw err;
    }
  },

  handleDrop({ dispatch }, event) {
    event.preventDefault();
    dispatch('processFiles', event.dataTransfer.files);
  },

  processFiles({ commit }, files) {
    const validFileTypes = [
      'text/plain',
      'text/markdown',
      'application/pdf',
      'text/html',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/csv',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/xml',
      'application/epub+zip',
      'message/rfc822',
      'application/vnd.ms-outlook',
    ];
    const maxFileSize = 15 * 1024 * 1024;

    commit('clearUploadErrors');

    const filesArray = Array.from(files);
    filesArray.forEach(file => {
      if (!validFileTypes.includes(file.type)) {
        commit('addUploadError', `File "${file.name}" has an invalid type.`);
      } else if (file.size > maxFileSize) {
        commit('addUploadError', `File "${file.name}" exceeds the maximum size of 15MB.`);
      }
    });

    const validFiles = filesArray.filter(
      file => validFileTypes.includes(file.type) && file.size <= maxFileSize
    );

    if (validFiles.length > 0) {
      commit('appendUploadedFiles', validFiles);
      commit('setIsUploaded', true);
    }
  },

  updateIsUploaded({ commit }, status) {
    commit('setIsUploaded', status);
  },

  handleRemoveFile({ commit }) {
    commit('removeUploadedFile');
    commit('removeCrawlResult');
    commit('setDatasetId', null);
    commit('resetAdvancedUploadOptions');
    commit('setKnowledgeGraphEnabled', true);
  },

  changePage({ commit, dispatch, state }, { page, datasetId }) {
    commit('setPagination', {
      currentPage: page,
      totalPages: state.pagination.totalPages,
      totalItems: state.pagination.totalItems,
      pageSize: 10,
    });
    dispatch('fetchDocuments', datasetId);
  },

  async fetchDocumentLists({ rootGetters }, { datasetId, page, limit }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const { data } = await axios.get(
        knowledgeProxy(accountId, `/chatbot/datasets/${datasetId}/documents`),
        { params: { page, limit } }
      );
      return data;
    } catch (err) {
      console.error('Error fetching documents:', err);
      throw err;
    }
  },

  async fetchDatasetDetails({ commit, rootGetters }, datasetId) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const { data } = await axios.get(
        knowledgeProxy(accountId, `/chatbot/accounts/${accountId}/datasets/${datasetId}`)
      );
      commit('setKnowledgeDetails', data);
      return data;
    } catch (err) {
      console.error('Error fetching dataset details:', err);
      throw err;
    }
  },

  async fetchDocuments({ commit, state, rootGetters }, datasetId) {
    const { currentPage = 1 } = state.pagination || {};
    const pageSize = 10;
    const accountId = rootGetters.getCurrentAccountId;

    try {
      const { data: json } = await axios.get(
        knowledgeProxy(accountId, `/chatbot/accounts/${accountId}/datasets/${datasetId}/documents`),
        { params: { page: currentPage, limit: pageSize } }
      );

      const totalItems = json.total || json.data.length;
      const totalPages = Math.ceil(totalItems / pageSize);

      commit('setPagination', { currentPage, totalPages, totalItems, pageSize });

      const tableData = json.data.map((obj, index) => ({
        index: (currentPage - 1) * pageSize + index + 1,
        fileName: obj.name,
        source: obj.data_source_type,
        wordCount: obj.true_word_count,
        retrievalCount: obj.hit_count,
        uploadTimeFormatted: obj.created_at_formatted,
        status: obj.display_status,
        id: obj.id,
        // Failure cause for the "Error processing" badge tooltip — not a
        // table column (Document.vue skips it when rendering cells).
        error: obj.error,
      }));

      commit('setTableBody', tableData);
      return json.data[0];
    } catch (err) {
      console.error('Error fetching documents:', err);
      throw err;
    }
  },

  async handleUpload({ rootGetters, state, dispatch, commit }, datasetId) {
    const accountId = rootGetters.getCurrentAccountId;
    commit('clearUploadErrors');

    if (state.crawlResult?.data?.length > 0) {
      await dispatch('convertToFile');
    }

    const filesToUpload =
      state.uploadedFiles?.length > 0
        ? state.uploadedFiles
        : state.uploadedFile
        ? [state.uploadedFile]
        : [];

    if (!filesToUpload.length) {
      useAlert('No files selected for upload.');
      return;
    }

    let hasError = false;

    for (const file of filesToUpload) {
      const formData = new FormData();
      formData.append('file', file);
      appendAdvancedUploadOptions(formData, state.advancedUploadOptions);
      try {
        await axios.post(
          knowledgeProxy(accountId, `/chatbot/account/${accountId}/datasets/${datasetId}/documents`),
          formData
        );
      } catch (err) {
        hasError = true;
        let errorMsg;
        if (err.response?.status === 413) {
          errorMsg = `File "${file.name}" is too large. The server rejected it.`;
        } else if (err.response?.status === 402) {
          // Lightrag ingestion limits (word limit / credits / etc.) — no field
          // distinguishes them, just surface the server's message verbatim.
          errorMsg = err.response?.data?.message || `File "${file.name}" was rejected.`;
        } else {
          errorMsg = `Error uploading "${file.name}": ${err.response?.statusText || err.message || 'Unknown error'}`;
        }
        commit('addUploadError', errorMsg);
        useAlert(errorMsg);
        console.error(err);
      }
    }

    if (!hasError) useAlert('Documents uploaded successfully.');
  },

  async handleDeleteDocument({ rootGetters }, { datasetId, document_id }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      await axios.delete(
        knowledgeProxy(accountId, `/chatbot/datasets/${datasetId}/documents/${document_id}`)
      );
    } catch (err) {
      console.error('Error deleting document:', err);
      useAlert('Error deleting document.');
      throw err;
    }
  },

  async fetchActiveKnowledge({ rootGetters, commit }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const { data } = await axios.get(
        knowledgeProxy(accountId, `/subscription/${accountId}/active_datasets`)
      );
      commit('setRestrictionStatus', data.status);
      commit('setActiveKnowledgeList', data.active_knowledges);
    } catch (err) {
      console.error('Error fetching active knowledge:', err);
      throw err;
    }
  },

  async fetchKnowledgeGraph({ rootGetters }, { datasetId, label, depth, max }) {
    const accountId = rootGetters.getCurrentAccountId;
    const params = {};
    if (label !== undefined) params.label = label;
    if (depth !== undefined) params.depth = depth;
    if (max !== undefined) params.max = max;
    try {
      const { data } = await axios.get(
        knowledgeProxy(accountId, `/chatbot/accounts/${accountId}/datasets/${datasetId}/graph`),
        { params }
      );
      // { nodes, edges, is_truncated } — kept view-local by the caller, not
      // committed to shared state, since the graph is only relevant to the
      // one screen that renders it.
      return data;
    } catch (err) {
      console.error('Error fetching knowledge graph:', err);
      throw err;
    }
  },

  async updateActiveKnowledge({ rootGetters }, idArray) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      await axios.post(
        knowledgeProxy(accountId, `/subscription/${accountId}/active_datasets`),
        { ids: idArray }
      );
    } catch (err) {
      console.error('Error updating active knowledge:', err);
      throw err;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
